import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { TASApplicationModelFieldsSignature } from 'tio-data/data/resources/tas-application.type';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/data/resources/tas-program-instance.type';
import type {
  GenericTemplateField,
  CustomFieldSignature,
  TemplateApprovalWorkflowApprover,
} from '../../types/tuition-assistance';
import type { TASCourseModelFieldsSignature } from 'tio-data/data/resources/tas-course.type';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';

/**
 * @remarks
 * This type serves as a catch-all for FieldsSignature types for the various models.
 * It serves a purpose for typing utility methods that operate on any one of the FieldsSignature types,
 * but it is not meant to be used as a type when you know which model you are working with.
 * In that case, you should use the specific FieldsSignature type for that model.
 */
export type TASFields =
  | TASProgramTemplateModelFieldsSignature
  | TASApplicationModelFieldsSignature
  | TASProgramInstanceModelFieldsSignature
  | TASCourseModelFieldsSignature;

export type FieldValue =
  string | boolean | number | null | Record<string, unknown> | TemplateApprovalWorkflowApprover;

export type TemplateFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FieldName =
  | TemplateFieldName
  | keyof TASApplicationModelFieldsSignature
  | keyof TASProgramInstanceModelFieldsSignature
  | keyof TASCourseModelFieldsSignature;

export type CustomFieldId =
  | `CUSTOM_FIELD_TAS_PROGRAM_INSTANCE_${number}`
  | `CUSTOM_FIELD_TAS_APPLICATION_${number}`
  | `CUSTOM_FIELD_TAS_COURSE_${number}`
  | null
  | undefined;

export function setSingleValueForTasField(
  fieldName: keyof TASProgramTemplateModelFieldsSignature,
  value: FieldValue | undefined,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASProgramInstanceModelFieldsSignature,
  value: FieldValue | undefined,
  fieldsObject: TASProgramInstanceModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASApplicationModelFieldsSignature,
  value: FieldValue | undefined,
  fieldsObject: TASApplicationModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASCourseModelFieldsSignature,
  value: FieldValue | undefined,
  fieldsObject: TASCourseModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: FieldName,
  value: FieldValue | undefined,
  fieldsObject: TASFields
): void {
  if (!fieldName || !fieldsObject) {
    return;
  }

  // @ts-expect-error - Cory this is the issue that is causing so many problems...
  // Element implicitly has an 'any' type because expression of type 'FieldName' can't be used to index type 'TASFields'.
  const existingFieldConfig = fieldsObject[fieldName] || {};

  const updatedValues = value === undefined ? [] : [value];

  // @ts-expect-error Figure out how to type to avoid this
  // "Type 'FieldValue' is not assignable to type 'never'. Type 'null' is not assignable to type 'never'."
  fieldsObject[fieldName] = { ...existingFieldConfig, values: updatedValues };
}

/**
 * Set the `values` array of a field on a fields EDITING COPY (a `trackedObject`
 * or a plain `{ ...record.fields }` clone that is reassigned onto the record
 * before save), preserving any other config already on that field. The
 * in-place, array-valued counterpart to
 * {@link setSingleValueForTasField} — used to bulk-seed a template's editable
 * fields from a plain key→value(s) map.
 *
 * `TASFields` is a heterogeneous per-key record, so a write indexed by a runtime
 * key lands on `never`; we widen to a string-indexable bag for the assignment —
 * the same localized cast used by {@link setTrackedTasFieldValues}. This keeps
 * the unsoundness in one reviewed place instead of an inline suppression at
 * every program-builder call site.
 */
export function setValuesForTasField<F extends TASFields>(
  fieldsObject: F,
  fieldName: keyof F,
  values: FieldValue[]
): void {
  if (!fieldName || !fieldsObject) {
    return;
  }
  const bag = fieldsObject as Record<keyof F, Record<string, unknown>>;
  bag[fieldName] = { ...bag[fieldName], values };
}

/**
 * WarpDrive-safe single-field write for a LIVE record.
 *
 * `setSingleValueForTasField` mutates the `fields` object in place, which
 * WarpDrive's cache does not track: the change is dropped from `serializePatch`
 * on save and is not reactive. This helper clones `record.fields`, applies the
 * value, then REASSIGNS `record.fields` so the write goes through `setAttr` and
 * is persisted. Use it instead of `setSingleValueForTasField(name, value,
 * record.fields)` whenever the target is a live record attribute rather than a
 * `trackedObject` editing copy that is reassigned onto the record before save.
 *
 * Calls accumulate: each invocation clones the just-reassigned `fields`, so
 * setting several values in sequence on the same record is safe.
 */
export function setTrackedTasFieldValue<F extends TASFields>(
  record: { fields: F },
  fieldName: keyof F,
  value: FieldValue
): void {
  const fields = { ...record.fields } as F;
  // `fieldName: keyof F` gives callers per-record safety (you can't set an
  // application field on a course/template record). The casts below are an
  // internal detail: `setSingleValueForTasField`'s public overloads can't
  // resolve against the generic union `F`, and its body is type-agnostic at
  // runtime, so we route through the application overload.
  setSingleValueForTasField(
    fieldName as unknown as keyof TASApplicationModelFieldsSignature,
    value,
    fields as unknown as TASApplicationModelFieldsSignature
  );
  record.fields = fields;
}

/**
 * WarpDrive-safe MULTI-value write for a LIVE record — the array-valued
 * counterpart to {@link setTrackedTasFieldValue}. Repeating fields (e.g.
 * `SCHOLARSHIPS_RECEIVED`) store every entry under a single field's `values`
 * array, so they can't go through `setSingleValueForTasField`, which wraps a
 * lone value as `values: [value]`. This clones `record.fields`, replaces the
 * target field's `values` with the supplied array (preserving any other config
 * on that field), then REASSIGNS `record.fields` so the write goes through
 * `setAttr` and is persisted. Mutating `record.fields[fieldName]` in place is
 * not tracked by the cache and is dropped from `serializePatch` on save.
 *
 * Calls accumulate: each invocation clones the just-reassigned `fields`, so
 * setting several fields in sequence on the same record is safe.
 */
export function setTrackedTasFieldValues<F extends TASFields>(
  record: { fields: F },
  fieldName: keyof F,
  values: FieldValue[]
): void {
  const fields = { ...record.fields } as F;
  // Cast to a string-indexable bag: the generic union `TASFields` collapses each
  // field's value type to `never` for writes, so we widen the access to index it.
  const bag = fields as Record<keyof F, Record<string, unknown>>;
  bag[fieldName] = { ...bag[fieldName], values };
  record.fields = fields;
}

/**
 * WarpDrive-safe merge of custom-field value updates into an existing custom
 * fields list. Custom fields persist via the `customFields` ARRAY attribute on
 * TAS records, and WarpDrive only includes an attribute in `serializePatch`
 * when its value is REASSIGNED to a new reference — so callers MUST do
 * `record.customFields = mergeCustomFieldValues(record.customFields, updates)`.
 *
 * This is pure: it never mutates `existing` or its objects. It returns a brand
 * new array of shallow-cloned field objects, with each update merged onto the
 * matching field by `label` + `province`. Updates with no match are appended
 * only when `appendNew` is true (the application/instance edit flows build the
 * list and append; the approver review flow edits existing fields only).
 */
export function mergeCustomFieldValues<T extends { label?: string; province?: string }>(
  existing: T[] | null | undefined,
  updates: T[],
  options: { appendNew?: boolean } = {}
): T[] {
  const result: T[] = (existing ?? []).map((field) => ({ ...field }));
  for (const update of updates) {
    const index = result.findIndex(
      (field) => field.label === update.label && field.province === update.province
    );
    if (index >= 0) {
      result[index] = { ...update };
    } else if (options.appendNew) {
      result.push({ ...update });
    }
  }
  return result;
}

export function updateTasTemplateFieldConfig(
  fieldName: TemplateFieldName,
  updatedObject: Partial<GenericTemplateField>,
  fieldsObject: TASProgramTemplateModelFieldsSignature
) {
  if (!fieldName || !fieldsObject) {
    return;
  }

  const existingFieldConfig = fieldsObject[fieldName] || {};

  // @ts-expect-error Figure out how to type so it can properly index the keys
  // "Element implicitly has an 'any' type because expression of type 'FieldName' can't be used to index type 'TASFields'."
  fieldsObject[fieldName] = { ...existingFieldConfig, ...updatedObject };
}

export function getSingleValueForTasField<
  T extends TASFields,
  F extends TASCourseModelFieldsSignature | FieldValue | undefined,
>(fieldName: FieldName, fieldsObject: T | undefined, defaultValue?: F): F {
  if (!fieldName || !fieldsObject) {
    return defaultValue as F;
  }
  // @ts-expect-error: Typescript cannot guarantee that a union of keys (from FieldName) will always match the union of object types (TASFields).
  // This was dealt with partially using the exported overloads above, which will enforce valid key/value pairs in the consuming code.
  // Yet this fallback TS error still happens.
  const value = fieldsObject[fieldName]?.values?.[0];

  if (value === undefined) {
    return defaultValue as F;
  }

  return value as F;
}

export function getAllValuesForTasField(
  fieldName: keyof TASProgramTemplateModelFieldsSignature,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASProgramInstanceModelFieldsSignature,
  fieldsObject: TASProgramInstanceModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASApplicationModelFieldsSignature,
  fieldsObject: TASApplicationModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASCourseModelFieldsSignature,
  fieldsObject: TASCourseModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(fieldName: FieldName, fieldsObject: TASFields) {
  if (!fieldName || !fieldsObject) {
    return [];
  }

  // @ts-expect-error: Typescript cannot guarantee that a union of keys (from FieldName) will always match the union of object types (TASFields).
  // This was dealt with partially using the exported overloads above, which will enforce valid key/value pairs in the consuming code.
  // Yet this fallback TS error still happens.
  return fieldsObject[fieldName]?.values || [];
}

/**
 * This function is used to get the config for a field on a program template.
 * It is used in the program builder to display the field config in the UI.
 * For convenience, it also returns the first value in the values array as `value`.
 * The return value is readonly.
 *
 * @param fieldName
 * @param fieldsObject
 * @returns TemplateFieldConfigAndValues
 *
 */

export type ReadonlyTemplateFieldConfigAndValues = Readonly<
  GenericTemplateField & { value: FieldValue | undefined; name: TemplateFieldName }
>;

function normalizeComponentTypeHint(componentType: unknown): string | undefined {
  if (typeof componentType !== 'string') {
    return undefined;
  }

  const normalizedHint = componentType.trim().toLowerCase();
  return normalizedHint.length > 0 ? normalizedHint : undefined;
}

function extractFieldComponentTypeHint(fieldConfig: unknown): string | undefined {
  if (!fieldConfig || typeof fieldConfig !== 'object') {
    return undefined;
  }

  const config = fieldConfig as Record<string, unknown>;
  const directHint = normalizeComponentTypeHint(config.component_type ?? config.componentType);
  if (directHint) {
    return directHint;
  }

  const uiHint = config.ui_hint ?? config.uiHint;
  if (!uiHint || typeof uiHint !== 'object') {
    return undefined;
  }

  const hintConfig = uiHint as Record<string, unknown>;
  return normalizeComponentTypeHint(hintConfig.component_type ?? hintConfig.componentType);
}

export function resolveTasFieldComponentType(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature,
  explicitHint?: string
): string | undefined {
  const normalizedExplicitHint = normalizeComponentTypeHint(explicitHint);
  if (normalizedExplicitHint) {
    return normalizedExplicitHint;
  }

  const metadataHint = extractFieldComponentTypeHint(fieldsObject?.[fieldName]);
  if (metadataHint) {
    return metadataHint;
  }

  if (fieldName === 'SCHOOL_INSTITUTION_NAME') {
    return 'school-select';
  }

  return undefined;
}

export function isTasFieldComponentType(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature,
  componentType: string,
  explicitHint?: string
) {
  const normalizedComponentType = normalizeComponentTypeHint(componentType);
  if (!normalizedComponentType) {
    return false;
  }

  return (
    resolveTasFieldComponentType(fieldName, fieldsObject, explicitHint) === normalizedComponentType
  );
}

export function getConfigForTasField(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): ReadonlyTemplateFieldConfigAndValues {
  if (!fieldName || !fieldsObject || !fieldsObject[fieldName]) {
    console.warn(`Unable to resolve config for TAS field ${fieldName}. Returning empty config.`);
    return {
      name: fieldName,
      label: '',
      province: 'PROGRAM',
      required: false,
      visible: false,
      values: [],
      value: undefined,
    };
  }
  const config = fieldsObject[fieldName] as GenericTemplateField;
  return { ...config, value: config.values?.[0], name: fieldName };
}

/**
 * When a TasProgramInstance, TasApplication, or TasCourse is created, we copy the relevant custom fields
 * from the program template and set them on the model at the time of creation.
 * (We copy these arrays from `instanceCustomFields`, `applicationCustomFields`, and `courseCustomFields` on TasProgramTemplate model)
 *
 * This does not handle the edge case where the custom fields on the parent TasProgramTemplate might be edited after
 * the child instance/application/course has already been created. We need to preserve the values they might have stored, but update
 * the overall array of fields to include new ones or any changes to requirements.
 *
 * IMPORTANT:
 * The arrays of fields passed as arguments to this function should already be filtered by the proper model type. For example,
 * if we are passing in `TasProgramInstance.customFields` as `existingFields`, then we should pass in `TasProgramTemplate.instanceCustomFields`
 * as the `templateCustomFields`. See mapping below:
 *         `TasProgramInstance.customFields` and `TasProgramTemplate.instanceCustomFields`
 *         `TasApplication.customFields` and `TasProgramTemplate.applicationCustomFields`
 *         `TasCourse.customFields` and `TasProgramTemplate.courseCustomFields`
 *
 */
export function copyFieldsAndUpdatePerProgramTemplate(
  existingCustomFields: CustomFieldSignature[] = [],
  templateCustomFields: CustomFieldSignature[] = []
): CustomFieldSignature[] {
  // Make a clean copy of the existing custom fields
  const resolvedFields = existingCustomFields.map((field) => {
    return { ...field };
  });

  // Loop through the custom fields defined by the program template
  // and check to see if they exist already.
  templateCustomFields.forEach((programField) => {
    const existingField = resolvedFields.find((instanceField) => {
      // TAS.TODO: This is extremely brittle. We need to add a more unique ID to custom fields in the program builder.
      return instanceField.label === programField.label;
    });

    // If the field does not exist yet, add it to the resolved fields.
    // Otherwise, update the existing field with the config from program template
    // but preserve the values stored.
    if (!existingField) {
      resolvedFields.push({ ...programField });
    } else {
      const indexOfMatchingField = resolvedFields.indexOf(existingField);
      resolvedFields[indexOfMatchingField] = { ...programField, values: existingField.values };
    }
  });

  // We are purposely not going to remove old custom fields that might have been present on the program template but were deleted
  // from the program config. This ensures that we do not lose information that a user might have input that might be relevant
  // to the timeframe in which their instance/application/course exists.

  return resolvedFields;
}

export function getCustomFieldObjectsForProvince(
  province: string,
  customFieldsArray: CustomFieldSignature[]
): CustomFieldSignature[] {
  // Filter customFields by province, sort by ordinal, map and return only necessary keys
  const customFields = customFieldsArray
    .filter((obj) => obj.province === province)
    .sort((a, b) => a.ordinal - b.ordinal);

  return customFields;
}

export function getSortedOptionsByFieldName(
  fieldName: TemplateFieldName | CustomFieldId,
  fieldOptions: TasFieldOptionModel[] = []
): TasFieldOptionModel[] {
  return fieldOptions
    .slice()
    .filter((option) => option.fieldName === fieldName)
    .sort((a, b) => {
      if (a.ordinal === b.ordinal) {
        return 0;
      }

      return a.ordinal > b.ordinal ? 1 : -1;
    });
}

export function getFieldLabelForFieldName(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): string {
  return fieldsObject[fieldName]?.label;
}

/**
 * Build the program-template portion of an approval-response picker for a given
 * response-text field (approve / reject / revise).
 *
 * Returns the field's multi-option TasFieldOption "response templates" whenever
 * any such records exist for the field. Otherwise falls back to the single
 * inline `fields` value — the classic program-builder "approver note" — surfaced
 * as ONE selectable option so callers can MERGE it with company canned responses.
 * Previously the inline note was only a last-ditch pre-fill, so a single tagged
 * company comment hid it entirely (approval modal + footer regression).
 *
 * `fallbackTitle` labels the inline-note option only when the field itself has
 * no label (callers pass a localized string).
 */
export function buildTemplateResponseOptions(
  fieldName: TemplateFieldName | null,
  fields: TASProgramTemplateModelFieldsSignature | undefined,
  tasFieldOptions: TasFieldOptionModel[] = [],
  fallbackTitle = ''
): { title: string; body: string }[] {
  if (!fieldName) {
    return [];
  }

  const optioned = getSortedOptionsByFieldName(fieldName, tasFieldOptions).map((option) => ({
    title: option.label,
    body: option.value,
  }));
  // Deliberate precedence, not a missing merge: once an admin has authored
  // multi-option response templates for this field, those ARE the program's
  // responses. Any leftover inline `fields` value is superseded config, so it is
  // dropped rather than appended as an extra option.
  if (optioned.length > 0) {
    return optioned;
  }

  if (!fields) {
    return [];
  }

  const value = getSingleValueForTasField(fieldName, fields);
  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }

  const label = getFieldLabelForFieldName(fieldName, fields);
  return [{ title: label || fallbackTitle, body: value }];
}

export default null; // silence a false warning
